import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, makeStyles, Theme } from '@material-ui/core';
import { ApiError, ProjectDevice } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import { DialogResponse, DialogResponseCode, DialogType, modeUIContext, setIsEditingForm, showDialog, showErrorNotification,
    showSuccessNotification } from '@moderepo/mode-ui-state';
import { capitalizeAll, getErrorMessage, useModeFormStyle } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById, selectSudoProjectAccessKey } from '@moderepo/mode-data-state-ritz';
import { updateHomeDevice, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { useRitzTranslation } from '../../utils/customHooks';
import { ProjectDevicesContainer } from '../projects/ProjectDevicesContainer';


const useStyle = makeStyles((theme: Theme) => {

    return {
        root: {
            width      : '100%',
            '&.editing': {
                '& $title': {
                    padding: theme.spacing(1),
                },
            },
        },

        title: {
            fontSize  : 'larger',
            margin    : theme.spacing(1, 0),
            fontWeight: 500,
            color     : theme.palette.text.primary,
        },

        deviceTablePanel: {
            width   : '100%',
            overflow: 'hidden',
        },

        form: {
            height: 'auto',
        },

        formActionContainer: {
            padding: theme.spacing(2),
        },
    };
}, { name: 'AddPreprovisionedDeviceContainer' });



/**
 * This is the list of device's fields we want to hide from the devices table. This list is static so we can define it outside of the component
 * for optimization
 */
const devicesTableHiddenFields = {
    preview         : true,
    homeId          : true,
    status          : true,
    creationTime    : true,
    connectivityTime: true,
    claimTime       : true,
};



interface AddPreprovisionedDeviceContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly homeId: number;
    readonly gatewaysOnly?: boolean;                // A flag to indicate that only show Gateways, not all devices. Default is FALSE, show all devices
    readonly onNewDeviceAdded: ()=> void;
    readonly onNewDeviceCanceled: ()=> void;
}


/**
 * This is the dialog used for getting an agent to select a device from the a project's set of devices.
 * @param props
 */
export const AddPreprovisionedDeviceContainer = (props: AddPreprovisionedDeviceContainerProps) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const projectKey = selectSudoProjectAccessKey(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const [isEditing, setIsEditing] = useState<boolean>(true);
    const [selectedDevice, setSelectedDevice] = useState<ProjectDevice | undefined>();
    const classes = useStyle();
    const formClasses = useModeFormStyle();


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * This component is used for selecting a device to add to a home so we must turn on edit mode as soon as this component start
     */
    useEffect(() => {
        onChangeEditingMode(true);
    }, [onChangeEditingMode]);



    const onDeviceSelected = useCallback((device: ProjectDevice) => {
        // Make sure the device is available
        if (!device.homeId) {
            setSelectedDevice(device);
        } else {
            setSelectedDevice(undefined);
        }
    }, []);


    const onCancelClicked = useCallback((event: React.MouseEvent) => {
        event.preventDefault();
        onChangeEditingMode(false);
        props.onNewDeviceCanceled();
        return false;
    }, [props, onChangeEditingMode]);



    /**
     * On "Select" device button clicked. We will add the selected device to the current home
     */
    const onOkayClicked = useCallback((event: React.MouseEvent) => {
        event.preventDefault();
        if (selectedDevice && !selectedDevice.homeId) {
            uiDispatch(showDialog({
                type          : DialogType.CONFIRM,
                title         : 'dialog.confirm_add_home_device.title',
                message       : 'dialog.confirm_add_home_device.message',
                messageData   : { device_name: (selectedDevice.name ? selectedDevice.name : `#${selectedDevice.id}`) },
                positiveButton: 'label.yes',
                negativeButton: 'label.cancel',
                onClose       : async (response?: DialogResponse): Promise<void> => {
                    if (response && response.code === DialogResponseCode.POSITIVE) {
                        try {
                            // To add an existing device to a home, we call update device API and pass it the new homeId. However, changing
                            // a device's homeId requires admin permission therefore we must use the projectKey
                            await appDataDispatch(updateHomeDevice(props.homeId, selectedDevice.id, { homeId: props.homeId }, projectKey?.token));

                            uiDispatch(showSuccessNotification('notif.device_added_successfully'));
                            props.onNewDeviceAdded();
                            onChangeEditingMode(false);
                        } catch (error) {
                            uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
                        }
                    }
                },
            }));
        } else if (selectedDevice && selectedDevice.homeId) {
            uiDispatch(showErrorNotification('notif.device_not_available'));
        }
        return false;
    }, [selectedDevice, uiDispatch, appDataDispatch, props, projectKey?.token, onChangeEditingMode]);



    return (
        <div className={clsx(props.className, classes.root, isEditing && 'editing')}>
            <div className={classes.title}>
                {capitalizeAll(trans(props.gatewaysOnly ? 'select_gateway.container.title' : 'select_device.container.title'))}
            </div>
            <form className={clsx(formClasses.root, classes.form)}>
                <ProjectDevicesContainer
                    className={classes.deviceTablePanel}
                    projectId={props.projectId}
                    title=""
                    hiddenFields={devicesTableHiddenFields}
                    gatewaysOnly={props.gatewaysOnly}
                    onDeviceSelected={onDeviceSelected}
                />
                <div className={clsx(formClasses.formActionContainer, classes.formActionContainer)}>
                    <div />
                    <div>
                        <Button
                            className={clsx(formClasses.formActionButton)}
                            size="small"
                            onClick={onCancelClicked}
                        >
                            {capitalizeAll(trans('label.cancel'))}
                        </Button>
                        <Button
                            className={clsx(formClasses.formActionButton)}
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={!selectedDevice}
                            onClick={onOkayClicked}
                            size="small"
                        >
                            {capitalizeAll(trans('label.select'))}
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    );
};
